import React, { useState } from 'react';
import classnames from 'classnames';

import { Typography, Link, Grow, Fade } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useUserContext } from '@hydra/common/context';
import { Image, Button, Translate } from '@hydra/common/components';

import { sendEvent } from '@hydra/utils/gtm';
import getHelpLink from '@hydra/utils/getHelpLink';

import { useI18n } from '@hydra/common/customHooks';

import ShadowSvg from '@hydra/assets/img/create/shadow.svg';
import ProjectStaticSvg from '@hydra/assets/img/create/project/project_static.svg';
import ProjectHoverSvg from '@hydra/assets/img/create/project/project_hover.svg';
import ReviewStaticSvg from '@hydra/assets/img/create/review/review_static.svg';
import ReviewHoverSvg from '@hydra/assets/img/create/review/review_hover.svg';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    flexGrow: 1,
    alignItems: 'stretch',
    marginTop: theme.spacing(3),
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row'
    }
  },
  wrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 300,
      maxWidth: 300
    },
    [theme.breakpoints.up('lg')]: {
      width: 350,
      maxWidth: 350
    }
  },
  boxCta: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    marginBottom: theme.spacing(6),
    borderRadius: theme.shape.borderRadius * 5,
    padding: 0,
    margin: 0,
    color: theme.palette.text.primary,
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
      margin: `${theme.spacing(1)}px ${theme.spacing(4)}px`
    },
    '&:hover, &:focus, &:active': {
      textDecoration: 'none !important',
      color: theme.palette.text.primary
    }
  },
  svgContainer: {
    width: 200,
    height: 150,
    [theme.breakpoints.up('sm')]: {
      width: 220,
      height: 160
    },
    [theme.breakpoints.up('lg')]: {
      width: 300,
      height: 225
    },
    position: 'relative'
  },
  ctaButton: {
    borderRadius: theme.shape.borderRadius * 5,
    height: theme.spacing(6),
    marginBottom: theme.spacing(1),
    fontWeight: theme.typography.fontWeightSemibold,
    fontSize: `${theme.spacing(2)}px !important`,
    textDecoration: 'none',
    transition: theme.transitions.create(
      ['background-color', 'color'],
      { duration: theme.transitions.duration.complex }
    ),
    '&:hover, &:focus, &:active': {
      textDecoration: 'none',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    }
  },
  svgGraph: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  hiddenXs: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block'
    }
  },
  title: {
    display: 'flex',
    justifyContent: 'center',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& span': {
      fontWeight: theme.typography.fontWeightSemibold
    }
  },
  learnmore: {
    width: '100%',
    textAlign: 'center',
    display: 'inline-block'
  },
  ctaDescription: {
    lineHeight: '1.8em',
    [theme.breakpoints.up('sm')]: {
      marginLeft: 0
    },
    maxWidth: 320
  },
  titleText: {
    color: theme.palette.primary.main,
    lineHeight: '2em',
    fontWeight: theme.typography.fontWeightSemibold
  },
  details: {
    backgroundColor: theme.palette.background.secondary,
    borderRadius: theme.shape.borderRadius * 2.5,
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    marginBottom: theme.spacing(4)
  },
  detailsTitle: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.fontSizeMedium,
    fontWeight: theme.typography.fontWeightSemibold
  },
  detailsText: {
    fontSize: theme.typography.fontSizeMedium,
    fontWeight: theme.typography.fontWeightRegular
  }
}));

const reviewLinks = {
  en: '/sections/360000012869-Reviews',
  es: '/sections/360000012869-Informes',
  fr: '/sections/360000012869-Rapports',
  nl: '/sections/360000012869-Reviews',
  pt: '/sections/360000012869-An%C3%A1lise',
  de: '/sections/360000012869-Analysen'
};

const projectLinks = {
  en: '/sections/360000012889-Projects',
  es: '/sections/360000012889-Proyectos',
  fr: '/sections/360000012889-Projets',
  nl: '/sections/360000012889-Projecten',
  pt: '/sections/360000012889-Projetos',
  de: '/sections/360000012889-Projects'
};

interface ISvgAnimatedGraphic {
  hover: boolean;
  staticGraphic: string
  hoverGraphic: string
}

function SvgAnimatedGraphic ({ hover, staticGraphic, hoverGraphic, ...props }: ISvgAnimatedGraphic) {
  const classes = useStyles();
  return (
    <div className={classes.svgContainer}>
      <Grow in={hover}>
        <Image src={hoverGraphic} className={classnames(classes.svgGraph, classes.hiddenXs)} />
      </Grow>
      <Image src={staticGraphic} className={classes.svgGraph} />
      <Fade in={hover}>
        <Image src={ShadowSvg} className={classnames(classes.svgGraph, classes.hiddenXs)} />
      </Fade>
    </div>
  );
}

interface ICreateMenu {
  projectCount: number
}

export default function CreateMenu ({ projectCount }: ICreateMenu) {
  const { locale } = useI18n();
  const classes = useStyles();
  const { user } = useUserContext();
  const maxProjects = user?.maxProjects || 0;
  const projectsLeft = maxProjects - projectCount;

  const [hoverProject, setHoverProject] = useState(false);
  const handleProjectHoverOver = () => setHoverProject(true);
  const handleProjectHoverOut = () => setHoverProject(false);

  const [hoverReview, setHoverReview] = useState(false);
  const handleReviewHoverOver = () => setHoverReview(true);
  const handleReviewHoverOut = () => setHoverReview(false);

  const gtmLabel = projectsLeft ? 'cflow-choice-project-btn' : 'overview-cflow-no-projects-left-cta';
  const CreateProjectBtnProps = {
    href: projectsLeft ? `/${locale}/create/project` : `/${locale}/user/plan?npl=1`,
    onClick: () => sendEvent(gtmLabel)
  };

  return (
    <>
      <div className={classes.title}>
        <Typography variant='h3' component='h1' align='center'>
          <Translate content='cflow_dialog_title' />
        </Typography>
      </div>
      <div className={classnames(
        'nr-cflow-create-menu',
        classes.root
      )}
      >
        <div className={classes.boxCta}>
          <div className={classes.wrapper}>
            <SvgAnimatedGraphic
              hover={hoverReview}
              staticGraphic={ReviewStaticSvg}
              hoverGraphic={ReviewHoverSvg}
            />
            <Typography variant='h3' className={classes.titleText} noWrap>
              <Translate content='cflow_dialog_cta_reviews_title' />
            </Typography>
            <Typography align='center' className={classes.ctaDescription} component='p' paragraph>
              <Translate content='cflow_dialog_review_description' />
            </Typography>
            <div className={classes.details}>
              <Typography
                align='center'
                className={classnames(
                  classes.ctaDescription,
                  classes.detailsText
                )}
                component='p'
              >
                <Translate content='cflow_dialog_review_details' />
              </Typography>
            </div>
          </div>
          <div>
            <Button
              onMouseEnter={handleReviewHoverOver}
              onMouseLeave={handleReviewHoverOut}
              fullWidth
              variant='outlined'
              color='primary'
              href={`/${locale}/create/review`}
              onClick={() => sendEvent('cflow-choice-review-btn')}
              className={classnames(
                'nr-cflow-review-cta',
                classes.ctaButton
              )}
            >
              <Translate content='cflow_dialog_cta_review_button' />
            </Button>
            <Link
              color='inherit'
              target='_blank'
              rel='noreferrer'
              className={classnames(
                classes.ctaDescription,
                classes.learnmore
              )}
              href={`${getHelpLink(locale)}${reviewLinks[locale]}`}
              onClick={() => sendEvent('cflow-choice-review-learnmore')}
            >
              <Translate content='cflow_dialog_review_learnmore' />
            </Link>
          </div>
        </div>
        <div className={classes.boxCta}>
          <div className={classes.wrapper}>
            <SvgAnimatedGraphic
              hover={hoverProject}
              staticGraphic={ProjectStaticSvg}
              hoverGraphic={ProjectHoverSvg}
            />
            <Typography variant='h3' color='primary' className={classes.titleText} noWrap>
              <Translate content='cflow_dialog_cta_projects_title' />
            </Typography>
            <Typography gutterBottom align='center' className={classes.ctaDescription} component='span' paragraph>
              <Translate content='cflow_dialog_project_description' />
            </Typography>
            <div className={classes.details}>
              <Typography
                align='center'
                className={classnames(
                  classes.ctaDescription,
                  classes.detailsTitle
                )}
                component='p'
              >
                <Translate content='cflow_dialog_project_details_title' />
              </Typography>
              <Typography
                align='center'
                className={classnames(
                  classes.ctaDescription,
                  classes.detailsText
                )}
                component='p'
              >
                <Translate content='cflow_dialog_project_details' />
              </Typography>
            </div>
          </div>
          <div>
            <Button
              onMouseEnter={handleProjectHoverOver}
              onMouseLeave={handleProjectHoverOut}
              fullWidth
              variant='outlined'
              color='primary'
              className={classnames(
                'nr-cflow-project-cta',
                classes.ctaButton
              )}
              {...CreateProjectBtnProps}
            >
              <Translate content='cflow_dialog_cta_project_button' />
            </Button>
            <Link
              color='inherit'
              target='_blank'
              rel='noreferrer'
              href={`${getHelpLink(locale)}${projectLinks[locale]}`}
              onClick={() => sendEvent('cflow-choice-project-learnmore')}
              className={classnames(
                classes.ctaDescription,
                classes.learnmore
              )}
            >
              <Translate content='cflow_dialog_project_learnmore' />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
